/** Turn a schema path like ["salary_min"] into "Salary min". */
function fieldLabel(path: unknown): string {
  const parts = Array.isArray(path) ? path.filter((p) => typeof p !== "number") : [];
  const last = parts.length ? String(parts[parts.length - 1]) : "";
  if (!last) return "This field";
  const words = last
    .replace(/[_.-]+/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

interface ZodIssueLike {
  code?: string;
  path?: unknown;
  message?: string;
  minimum?: number;
  maximum?: number;
  type?: string;
  options?: unknown[];
  validation?: string;
  format?: string;
}

/** Human sentence for a single Zod issue. Never leaks codes or objects. */
function issueSentence(issue: ZodIssueLike): string {
  const label = fieldLabel(issue.path);
  const unit = issue.type === "string" ? "characters" : issue.type === "array" ? "items" : "";
  switch (issue.code) {
    case "too_small":
      if (issue.minimum === 1 && unit === "characters") return `${label} is required.`;
      return unit
        ? `${label} must be at least ${issue.minimum} ${unit}.`
        : `${label} must be ${issue.minimum} or more.`;
    case "too_big":
      return unit
        ? `${label} must be ${issue.maximum} ${unit} or fewer.`
        : `${label} must be ${issue.maximum} or less.`;
    case "invalid_type":
      return `${label} is required.`;
    case "invalid_enum_value":
    case "invalid_value":
      return `${label} has an option that isn't allowed. Pick one from the list.`;
    case "invalid_string":
    case "invalid_format":
      if (issue.validation === "url" || issue.format === "url")
        return `${label} must be a valid link starting with https://`;
      if (issue.validation === "email") return `${label} must be a valid email address.`;
      if (issue.validation === "datetime") return `${label} must be a valid date and time.`;
      return `${label} isn't in the expected format.`;
    default: {
      const msg = typeof issue.message === "string" ? issue.message : "";
      if (msg && !/^(Required|Invalid)/i.test(msg) && !msg.includes("{")) return `${label}: ${msg}`;
      return `${label} isn't valid.`;
    }
  }
}

/** Parse a message that is actually a serialized array of Zod issues. */
function zodIssuesFromMessage(message: string): ZodIssueLike[] | null {
  const text = message.trim();
  if (!text.startsWith("[") && !text.startsWith("{")) return null;
  try {
    const parsed: unknown = JSON.parse(text);
    const arr = Array.isArray(parsed)
      ? parsed
      : Array.isArray((parsed as { issues?: unknown[] })?.issues)
        ? (parsed as { issues: unknown[] }).issues
        : null;
    if (!arr?.length) return null;
    const issues = arr.filter(
      (i): i is ZodIssueLike => !!i && typeof i === "object" && "path" in (i as object),
    );
    return issues.length ? issues : null;
  } catch {
    return null;
  }
}

/**
 * Convert validation failures (Zod issue arrays, raw JSON strings) into short,
 * human-readable sentences. Returns an empty array when `e` isn't a validation
 * error, so callers can fall back to `errMessage`.
 */
export function validationMessages(e: unknown): string[] {
  const raw = e as { issues?: unknown; message?: unknown; name?: unknown } | null;
  const direct = Array.isArray(raw?.issues) ? (raw?.issues as ZodIssueLike[]) : null;
  const fromMessage =
    !direct && typeof raw?.message === "string" ? zodIssuesFromMessage(raw.message) : null;
  const fromString = typeof e === "string" ? zodIssuesFromMessage(e) : null;
  const issues = direct ?? fromMessage ?? fromString;
  if (!issues) return [];
  const seen = new Set<string>();
  for (const issue of issues) seen.add(issueSentence(issue));
  return [...seen].slice(0, 5);
}

export function errMessage(e: unknown, fallback = "Failed"): string {
  const validation = validationMessages(e);
  if (validation.length) return validation.join(" ");
  if (e instanceof Error && e.message) {
    // Never surface serialized objects or stack traces to users.
    const m = e.message.trim();
    if (m.startsWith("{") || m.startsWith("[") || /\n\s+at\s/.test(m)) return fallback;
    return m;
  }
  if (typeof e === "string" && e && !e.trim().startsWith("{") && !e.trim().startsWith("["))
    return e;
  return fallback;
}
